/* Shared state for the «Начало работы» setup checklist (spec 20260909 §3, §5).
 *
 * The server decides what is done (`GET /t/{slug}/setup-status`), so the
 * Dashboard panel, the Settings page and the wizard's closing screen show the
 * same groups and the same percentage. This module only caches the payload,
 * remembers whether the panel is minimised, and forwards «Пропустить».
 */

import { get, writable, type Writable } from "svelte/store";
import { api } from "./api";

export type SetupItem = {
  id: string;
  required: boolean;
  once: boolean;
  pro: boolean;
  done: boolean;
  skipped: boolean;
  locked: boolean;
  href: string;
  cta: string;
};

export type SetupGroup = {
  id: string;
  items: SetupItem[];
  done: number;
  total: number;
  complete: boolean;
};

export type SetupHint = { id: string; params: Record<string, unknown> };

export type SetupStatus = {
  version: number;
  role: "seller" | "buyer";
  pro: boolean;
  trial_ends_at: string | null;
  groups: SetupGroup[];
  required_total: number;
  required_done: number;
  percent: number;
  hints: SetupHint[];
};

export type ChecklistState = {
  slug: string;
  loaded: boolean;
  /** Panel minimised to its one-line summary — never a permanent dismissal. */
  collapsed: boolean;
  status: SetupStatus | null;
};

const EMPTY: ChecklistState = { slug: "", loaded: false, collapsed: false, status: null };

export const checklist: Writable<ChecklistState> = writable(EMPTY);

// Key kept from when this flag meant "dismissed" so existing users don't have
// their panel pop back open on the next deploy.
const collapsedKey = (slug: string) => `oe_onboard_dismissed_${slug}`;

function readCollapsed(slug: string): boolean {
  try {
    return localStorage.getItem(collapsedKey(slug)) === "1";
  } catch {
    return false;
  }
}

/** Fetch progress for `slug`. Cheap to call from several components: a state
 *  already loaded for the same tenant is reused unless `force` is set. */
export async function loadChecklist(slug: string, force = false): Promise<void> {
  if (!slug) return;
  const current = get(checklist);
  if (!force && current.slug === slug && current.loaded) return;
  if (current.slug !== slug) {
    checklist.set({ slug, loaded: false, collapsed: readCollapsed(slug), status: null });
  }
  try {
    const status = await api.get<SetupStatus>(`/t/${slug}/setup-status`);
    checklist.set({ slug, loaded: true, collapsed: readCollapsed(slug), status });
  } catch {
    // Non-critical: a failed load leaves the previous payload (or nothing) in
    // place; every consumer already copes with `status === null`.
    checklist.update((s) => (s.slug === slug ? { ...s, loaded: true } : s));
  }
}

/** «Пропустить» / «Вернуть» an optional item. The server answers with the
 *  refreshed payload, so the store is updated in the same round trip. */
export async function skipItem(slug: string, item: string, skipped: boolean): Promise<void> {
  const status = await api.post<SetupStatus>(`/t/${slug}/setup-status/skip`, { item, skipped });
  checklist.update((s) => (s.slug === slug ? { ...s, loaded: true, status } : s));
}

/** Minimise the panel to its summary bar. */
export function collapseChecklist(slug: string): void {
  try {
    localStorage.setItem(collapsedKey(slug), "1");
  } catch {
    /* ignore */
  }
  checklist.update((s) => (s.slug === slug ? { ...s, collapsed: true } : s));
}

/** Expand the panel again from its summary or the Dashboard guide button. */
export function expandChecklist(slug: string): void {
  try {
    localStorage.removeItem(collapsedKey(slug));
  } catch {
    /* ignore */
  }
  checklist.update((s) => (s.slug === slug ? { ...s, collapsed: false } : s));
}

/** Completion over the required items, as a whole percentage. 0 while loading. */
export function percentComplete(s: ChecklistState): number {
  return s.loaded && s.status ? s.status.percent : 0;
}

export const doneCount = (s: ChecklistState): number => s.status?.required_done ?? 0;
export const totalCount = (s: ChecklistState): number => s.status?.required_total ?? 0;

/** The first group that still has something to do — the one to show open. */
export function currentGroupId(status: SetupStatus | null): string | null {
  if (!status) return null;
  const open = status.groups.find((g) => !g.complete);
  return open ? open.id : null;
}

/** A hint by id, if the server raised it for this company. */
export function hintFor(s: ChecklistState, id: string): SetupHint | undefined {
  return s.status?.hints.find((h) => h.id === id);
}
